import { useCallback, useEffect, useState } from "react";
import { Crown, Images, Sparkles, Quote, Newspaper, Inbox, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/items/gallery"),
      api.get("/admin/items/services"),
      api.get("/admin/items/testimonials"),
      api.get("/admin/items/posts"),
      api.get("/admin/messages"),
    ]).then(([g, s, t, p, m]) => {
      setCounts({ gallery: g.data.length, services: s.data.length, testimonials: t.data.length, posts: p.data.length, messages: m.data.length });
      setRecent(m.data.slice(0, 5));
    }).catch(() => setCounts({}));
  }, []);

  const cards = [
    { label: "Gallery Photos", value: counts?.gallery, icon: Images, to: "/admin/gallery", testid: "dash-card-gallery" },
    { label: "Pageant Categories", value: counts?.services, icon: Sparkles, to: "/admin/services", testid: "dash-card-services" },
    { label: "Testimonials", value: counts?.testimonials, icon: Quote, to: "/admin/testimonials", testid: "dash-card-testimonials" },
    { label: "Blog Posts", value: counts?.posts, icon: Newspaper, to: "/admin/blog", testid: "dash-card-blog" },
    { label: "Messages", value: counts?.messages, icon: Inbox, to: "/admin/messages", testid: "dash-card-messages" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-10 flex items-center gap-4">
        <Crown className="h-8 w-8 text-[#D4AF37]" strokeWidth={1} />
        <div>
          <h1 className="font-display text-4xl font-medium text-white">Welcome, {user?.name || "Admin"}</h1>
          <p className="mt-1 text-sm font-light text-neutral-400">Everything on your website, governed from one court.</p>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            data-testid={c.testid}
            className="group border border-white/10 bg-[#0C0C0C] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/50"
          >
            <c.icon className="h-6 w-6 text-[#D4AF37]" strokeWidth={1} />
            <div className="font-display mt-4 text-4xl text-white">{c.value ?? "—"}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-neutral-400">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-12">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl text-white">Latest Enquiries</h2>
          <Link to="/admin/messages" data-testid="dash-view-all-messages" className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37] hover:gap-3 transition-all">
            View All <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div data-testid="dash-no-messages" className="border border-dashed border-white/15 py-12 text-center text-sm text-neutral-500">
            No enquiries yet. New contact form submissions will appear here.
          </div>
        ) : (
          <div className="divide-y divide-white/5 border border-white/10 bg-[#0C0C0C]">
            {recent.map((m, i) => (
              <div key={m.id} data-testid={`dash-message-${i}`} className="flex items-center justify-between gap-4 px-6 py-4">
                <div className="min-w-0">
                  <span className="text-sm text-white">{m.name}</span>
                  <span className="ml-3 text-xs text-[#D4AF37]/80">{m.category}</span>
                  <div className="mt-1 truncate text-xs font-light text-neutral-500">{m.message}</div>
                </div>
                {!m.read && <span className="shrink-0 bg-[#D4AF37] px-2 py-0.5 text-[9px] uppercase tracking-widest text-black">New</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
